import {List, Record} from 'immutable';

import {makeReducer, match} from 'web-app/util/redux';
import {PinLoadingState} from 'web-app/react/components/pin/pin';

import * as Actions from './actions';
import {type Account, type ExistingLogin, type LoginError, LoginErrorState, type ChallengeContext} from './models';
import * as Constants from './constants';

type ChallengeContextState = {show: false} | {show: true; challenge: ChallengeContext; loginTLD?: string};

export interface LoginStateProps {
    // Common state
    loading: boolean;
    removingAccount: boolean;
    forceCredentials: boolean;
    errorState?: LoginError;

    // State for token authentication
    loadingTokenAuthentication: boolean;

    // State for the carousel
    deviceAccounts: List<Account>;
    selectedAccountId: Account['loginId'];
    loadingDeviceAccounts: boolean;

    // State for the PIN component
    pin?: string;
    pinState?: PinLoadingState;

    // State for checking email against other domains
    lastCheckedEmail?: string;
    existingLogins: List<ExistingLogin>;
    fetchingExistingLogins: boolean;

    magicLinkSent: boolean;
    challengeContext: ChallengeContextState;
}
export class LoginState extends Record<LoginStateProps>({
    loading: false,
    removingAccount: false,
    forceCredentials: false,
    errorState: undefined,

    loadingTokenAuthentication: false,

    deviceAccounts: List(),
    selectedAccountId: Constants.NEW_ACCOUNT_IDENTIFIER,
    loadingDeviceAccounts: false,

    pin: undefined,
    pinState: undefined,

    lastCheckedEmail: undefined,
    existingLogins: List(),
    fetchingExistingLogins: false,

    magicLinkSent: false,
    challengeContext: {show: false},
}) {}

export default makeReducer<LoginState>(
    [
        match(Actions.reset, () => new LoginState()),
        match(Actions.setForceCredentials, (state, {payload}) =>
            state.merge({
                forceCredentials: payload.force,
            }),
        ),
        match(Actions.changePin, (state, {payload}) =>
            state.merge({
                pin: payload.pin,
                pinState: undefined,
            }),
        ),
        match(Actions.selectAccount, (state, {payload}) =>
            state.merge({
                selectedAccountId: payload.loginId,
                pinState: undefined,
                forceCredentials: false,
                errorState: undefined,
            }),
        ),
        match(Actions.credentialLogin, state =>
            state.merge({
                loading: true,
            }),
        ),
        match(Actions.credentialLoginSuccess, state =>
            state.merge({
                loading: false,
                errorState: undefined,
            }),
        ),
        match(Actions.credentialLoginFailed, (state, {payload}) =>
            state.merge({
                loading: false,
                errorState: payload.error,
            }),
        ),
        match(Actions.credentialLoginAborted, state =>
            state.merge({
                loading: false,
                errorState: undefined,
            }),
        ),
        match(Actions.tokenLogin, state =>
            state.merge({
                loadingTokenAuthentication: true,
                loading: true,
            }),
        ),
        match(Actions.tokenLoginSuccess, state =>
            state.merge({
                loadingTokenAuthentication: false,
                loading: false,
                errorState: undefined,
            }),
        ),
        match(Actions.tokenLoginFailed, (state, {payload}) =>
            state.merge({
                loadingTokenAuthentication: false,
                loading: false,
                errorState: payload.error,
            }),
        ),
        match(Actions.magicLinkLogin, state =>
            state.merge({
                loading: true,
                magicLinkSent: false,
            }),
        ),
        match(Actions.magicLinkLoginSuccess, state =>
            state.merge({
                loading: false,
                magicLinkSent: true,
            }),
        ),
        match(Actions.magicLinkLoginFailed, state =>
            state.merge({
                loading: false,
            }),
        ),
        match(Actions.fetchLoginsForDevice, state =>
            state.merge({
                loadingDeviceAccounts: true,
                errorState: undefined,
            }),
        ),
        match(Actions.fetchLoginsForDeviceFailed, state => state.merge({loadingDeviceAccounts: false})),
        match(Actions.fetchLoginsForDeviceSuccess, (state, {payload}) =>
            state.merge({
                deviceAccounts: payload.accounts,
                selectedAccountId: payload.accounts.some(acc => acc.loginId === payload.loginId)
                    ? payload.loginId
                    : Constants.NEW_ACCOUNT_IDENTIFIER,
                loadingDeviceAccounts: false,
            }),
        ),
        match(Actions.pinLogin, state =>
            state.merge({
                pinState: PinLoadingState.Loading,
                errorState: undefined,
            }),
        ),
        match(Actions.pinLoginSuccess, state => state.merge({pinState: PinLoadingState.Success})),
        match(Actions.pinLoginFailed, (state, {payload}) =>
            state.merge({
                pinState: PinLoadingState.Error,
                pin: undefined,
                errorState: payload.error,
                forceCredentials:
                    (payload.error &&
                        (payload.error.errorCode === LoginErrorState.TOO_MANY_PIN_ATTEMPTS ||
                            payload.error.errorCode === LoginErrorState.FORCE_CREDENTIAL_LOGIN)) ||
                    state.forceCredentials,
            }),
        ),
        match(Actions.removeAccount, state => state.merge({removingAccount: true})),
        match(Actions.removeAccountSuccess, (state, {payload}) => {
            const deviceAccounts = state.deviceAccounts.delete(
                state.deviceAccounts.findIndex(account => account.loginId === payload.loginId),
            );
            return state.merge({
                removingAccount: false,
                deviceAccounts,
                selectedAccountId:
                    deviceAccounts.size === 0 ? Constants.NEW_ACCOUNT_IDENTIFIER : state.selectedAccountId,
            });
        }),
        match(Actions.removeAccountFailed, state => state.merge({removingAccount: false})),
        match(Actions.testEmailForLogins, state =>
            state.merge({
                existingLogins: List(),
                fetchingExistingLogins: true,
            }),
        ),
        match(Actions.testEmailForLoginsSuccess, (state, {payload}) => {
            return state.merge({
                lastCheckedEmail: payload.email,
                existingLogins: payload.logins,
                fetchingExistingLogins: false,
            });
        }),
        match(Actions.testEmailForLoginsFailed, state =>
            state.merge({
                fetchingExistingLogins: false,
            }),
        ),
        match(Actions.resetEmailTest, state =>
            state.merge({
                existingLogins: List(),
                fetchingExistingLogins: false,
            }),
        ),
        match(Actions.resetError, state =>
            state.merge({
                errorState: undefined,
            }),
        ),
        match(Actions.clearMagicLinkSent, state =>
            state.merge({
                magicLinkSent: false,
            }),
        ),
        match(Actions.showChallenge, (state, {payload}) =>
            state.merge({
                challengeContext: {show: true, challenge: payload.challenge, loginTLD: payload.loginTLD},
                loading: false,
            }),
        ),
        match(Actions.resetChallenge, state =>
            state.merge({
                challengeContext: {show: false},
                loading: false,
            }),
        ),
    ],
    new LoginState(),
);
